<template>
  <main class="flex h-screen min-h-screen w-screen items-center justify-center bg-gray-50">
    <div
      class="container mx-auto flex flex-col items-center justify-center p-5 text-gray-700 md:flex-row"
      role="status"
      aria-live="polite"
    >
      <div class="max-w-md text-center md:text-left">
        <h1 class="text-lg font-bold md:text-4xl">{{ t('documentCompleted') }}</h1>
        <p class="font-semi-bold mt-6 text-xl leading-normal md:text-3xl">
          {{ t('documentCompletedMessage') }}
        </p>
        <p class="mb-8"></p>

        <div class="flex flex-col items-center space-y-4 md:items-start">
          <p class="text-gray-500">{{ t('needCopy') }}</p>

          <div class="flex space-x-4">
            <button
              v-if="showBackButton"
              class="flex items-center rounded-lg border border-gray-200 border-transparent px-4 py-2 text-sm font-medium
                leading-5 text-gray-700 shadow transition-colors duration-150 focus:ring-2 focus:ring-offset-2
                md:text-base"
              :class="`hover:border-${themeColor}-600 hover:text-${themeColor}-600 focus:ring-${themeColor}-500`"
              @click="navigateToHome"
            >
              <svg
                class="mr-2 h-5 w-5"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"
                />
              </svg>
              {{ t('backToPortal') }}
            </button>

            <button
              class="rounded-lg border border-transparent px-4 py-2 text-sm font-medium leading-5 text-white shadow
                transition-colors duration-150 focus:ring-2 focus:ring-offset-2 md:text-base"
              :class="`bg-${themeColor}-600 active:bg-${themeColor}-600 hover:bg-${themeColor}-700
                focus:ring-${themeColor}-500`"
              @click="handleDownloadClick"
            >
              {{ t('pdfDownload') }}
            </button>
          </div>
        </div>
      </div>
      <div
        class="mt-10 max-w-lg md:mt-0"
        aria-hidden="true"
      >
        <svg
          class="h-auto w-full"
          viewBox="0 0 960 960"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M700.795 370.415C712.98 371.615 725.388 372.815 737.38 370.367C749.372 367.919 761.121 361.23 766.68 350.322C772.633 338.611 770.523 324.655 769.187 311.599C766.235 282.757 767.697 253.024 758.473 225.547C757.36 222.217 756.073 218.878 755.831 215.365C755.589 211.852 756.586 208.048 759.296 205.812C761.783 203.77 765.161 203.393 768.423 203.257C768.761 203.238 769.1 203.218 769.429 203.209C776.533 202.976 783.644 202.741 790.761 202.502C790.819 202.494 790.877 202.49 790.935 202.492C797.135 202.28 804.127 202.367 808.482 206.792C812.082 210.45 812.75 215.992 813.205 221.097C817.386 267.844 819.593 314.94 815.344 361.677C814.589 369.977 813.583 378.412 810.205 386.038C806.033 395.438 798.474 402.976 790.073 408.909C773.996 420.172 754.803 426.134 735.173 425.962L735.163 426.988C735.429 459.335 739.397 491.544 746.99 522.988C748.442 528.979 749.623 536.334 744.99 540.388C742.057 542.962 737.818 543.146 733.937 543.488C720.844 544.642 708.207 548.866 697.052 555.818C697.042 549.827 697.042 543.836 697.052 537.845C697.042 533.558 697.046 529.274 697.062 524.992C697.168 473.531 697.781 422.08 698.901 370.639L699.307 369.419L700.795 370.415Z"
            fill="#171717"
          />
          <path
            d="M774.818 406.475C787.439 402.63 796.846 391.53 801.725 379.275C806.604 367.02 807.578 353.593 808.095 340.409C809.695 299.678 807.432 258.882 803.755 218.285"
            stroke="white"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M570.671 264.667C567.612 281.508 567.312 299.767 575.558 314.773C581.782 326.097 592.389 334.517 603.936 340.315C615.444 346.083 627.958 349.49 640.376 352.858C646.415 361.84 646.657 373.415 646.105 384.226C645.205 401.754 642.747 419.156 640.298 436.539C636.582 462.855 632.869 489.171 629.158 515.487C628.665 518.942 628.178 522.397 627.697 525.852L626.477 534.476C626.434 535.745 625.978 536.965 625.177 537.951C623.358 539.741 620.328 538.483 618.151 537.157C613.362 534.346 608.753 531.24 604.351 527.857C598.728 523.376 593.927 518.14 591.314 511.5C588.081 503.264 588.749 494.059 589.714 485.262C593.073 454.716 599.431 424.577 605.814 394.506C579.944 394.429 556.56 376.543 543.562 354.146C530.506 331.673 526.247 305.246 523.953 279.346C521.834 255.537 521.253 230.972 529.006 208.354C530.312 204.521 532.083 200.495 535.577 198.443C539.129 196.352 543.577 196.807 547.666 197.33L551.295 197.794C557.761 198.626 564.23 199.456 570.701 200.282C572.053 200.425 573.393 200.67 574.708 201.017C575.97 201.334 577.146 201.926 578.153 202.75C581.318 205.45 581.405 210.25 580.999 214.393C579.321 231.431 573.737 247.827 570.671 264.667Z"
            fill="#171717"
          />
          <path
            d="M615.094 352.961C586.637 349.036 564.365 323.311 558.584 295.171C552.803 267.031 561.13 237.514 575.264 212.505"
            stroke="white"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M169.406 583.159C176.859 581.969 184.456 583.672 191.754 585.617C210.754 590.679 229.326 597.445 248.664 600.977C268.002 604.509 288.617 604.626 306.609 596.719C324.447 588.879 339.09 571.951 340.252 552.546C344.055 552.485 347.842 552.031 351.552 551.191C377.519 545.761 402.587 536.808 427.552 527.798L427.861 528.659C445.118 548.016 462.82 580.081 469.595 605.11C476.37 630.139 478.838 656.135 480.067 682.035C481.767 717.904 481.122 754.198 473.137 789.215C470.679 800.045 467.446 810.866 461.514 820.254C458.028 825.844 453.475 830.691 448.114 834.52C444.403 837.139 440.243 839.055 435.841 840.173C426.26 842.534 415.274 840.095 408.412 833.185C407.545 832.319 406.752 831.382 406.041 830.385C399.498 821.277 400.176 809.014 401.376 797.865C405.238 761.793 411.576 725.905 412.535 689.639C413.494 653.373 410.035 616.305 393.035 584.259H393.026C391.167 596.503 389.048 608.727 385.137 620.459C380.598 634.106 373.494 647.182 362.77 656.759C349.22 668.867 331.083 674.452 313.226 677.82C280.453 683.842 246.865 683.934 214.06 678.091C197.538 675.207 180.891 670.764 167.032 661.328C161.855 657.818 157.219 653.571 153.271 648.721C146.571 640.456 142.141 630.506 141.763 619.947C141.532 610.267 144.933 600.851 151.297 593.554C155.931 588.077 162.339 584.399 169.406 583.159Z"
            fill="#171717"
          />
          <path
            d="M461.711 785.872C483.934 712.333 476.403 630.26 441.17 561.991"
            stroke="white"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M714.113 859.591C715.691 867.304 717.23 875.56 714.249 882.858C711.578 889.391 705.703 894.008 699.849 897.966C690.062 904.592 679.645 910.236 668.749 914.816C658.649 919.065 646.949 922.356 636.974 917.816C632.203 915.638 628.06 911.341 627.547 906.116C627.17 902.284 628.777 898.499 630.947 895.305C634.925 889.44 640.722 885.095 646.413 880.865C654.436 874.903 662.463 868.941 670.493 862.979L670.725 862.273C676.194 863.973 682.707 863.973 688.515 863.608C696.596 863.108 705.384 862.195 712.75 859.156L714.113 859.591Z"
            fill="#171717"
          />
          <path
            d="M676.435 263.641C666.281 260.97 656.128 258.296 645.976 255.618C642.966 254.824 639.953 254.03 636.937 253.237C634.686 252.793 632.538 251.93 630.607 250.692C628.768 249.337 627.423 247.052 627.849 244.807C628.603 240.819 634.091 239.726 637.875 241.207C641.659 242.688 644.709 245.727 648.58 246.956C646.838 244.923 645.038 242.784 644.37 240.181C643.702 237.578 644.534 234.374 646.954 233.202C649.267 232.08 652.103 233.183 654.01 234.896C655.917 236.609 657.262 238.884 659.062 240.713C663.417 245.107 669.882 246.443 675.883 247.924C682.609 249.586 689.22 251.68 695.676 254.195C698.424 255.26 701.276 256.518 702.993 258.918C706.129 263.318 704.144 269.342 702.886 274.607C700.051 286.783 701.889 299.579 708.035 310.466C709.148 312.421 710.335 315.066 708.78 316.68C707.203 318.306 704.348 317.086 702.915 315.325C701.482 313.564 700.505 311.279 698.473 310.253C698.744 312.837 700.534 314.967 701.628 317.319C702.722 319.671 702.847 323.01 700.612 324.336C698.899 325.362 696.566 324.578 695.212 323.097C693.98 321.513 693.125 319.669 692.712 317.706L692.17 317.628C693.689 306.954 694.552 296.197 694.754 285.418C694.832 281.063 694.745 276.466 692.509 272.73C689.27 267.3 682.552 265.258 676.435 263.641Z"
            fill="#171717"
          />
          <path
            d="M668.277 355.171C664.638 358.375 660.996 361.579 657.35 364.782C657.456 359.169 657.559 353.559 657.659 347.951L658.308 347.961C661.589 349.316 664.86 351.629 668.141 352.984C668.493 353.08 668.797 353.304 668.993 353.613C669.245 354.175 668.741 354.765 668.277 355.171Z"
            fill="#171717"
          />
          <path
            d="M650.302 405.974C649.344 396.644 646.392 377.122 653.476 369.35C656.264 366.302 660 367.134 662.845 369.883C666.765 373.657 665.145 379.948 665.236 385.136C665.836 420.539 672.718 455.554 685.561 488.551C684.419 493.662 683.28 498.772 682.144 503.882C677.014 504.076 672.253 500.782 669.33 496.565C666.407 492.348 665.023 487.274 663.755 482.299C657.399 457.223 652.902 431.712 650.302 405.974V405.974Z"
            fill="#171717"
          />
          <path
            d="M561.184 834.936C551.592 730.999 565.655 625.836 603.731 528.659L604.35 527.859C608.752 531.243 613.361 534.349 618.15 537.16C620.327 538.486 623.357 539.743 625.176 537.953C625.976 536.968 626.433 535.748 626.476 534.478L627.696 525.854C650.105 533.217 673.471 537.257 697.052 537.846C697.042 543.838 697.042 549.829 697.052 555.819C704.597 551.112 712.848 547.642 721.49 545.541L721.606 545.986C714.744 638.522 726.659 731.31 729.562 824.06C729.833 832.751 729.872 842.033 725.162 849.33C722.136 853.804 717.802 857.235 712.754 859.155C705.388 862.194 696.6 863.103 688.519 863.606C682.711 863.974 676.198 863.975 670.729 862.272C666.577 860.984 663.029 858.709 660.848 854.871C658.457 850.641 658.467 845.531 658.548 840.671C659.864 753.845 661.186 667.016 662.516 580.184L662.197 579.96C637.672 614.455 634.468 650.015 632.039 692.697C629.551 736.197 632.368 780.075 625.999 823.173C625.138 829 624.025 835.029 620.637 839.858C618.489 842.849 615.668 845.292 612.403 846.991C606.218 850.35 598.603 851.735 591.497 852.819C585.187 853.777 577.889 854.318 572.072 852.363C568.803 851.37 565.989 849.255 564.126 846.392C562.006 843.019 561.552 838.891 561.184 834.936Z"
            :class="`text-${themeColor}-600 fill-current`"
          />
          <path
            d="M614.348 847.219C617.29 856.742 620.136 867.534 615.316 876.273C611.793 882.673 604.892 886.417 598.116 889.165C577.738 897.387 555.474 899.777 533.816 896.065C531.629 895.678 529.344 895.194 527.631 893.765C525.337 891.858 524.563 888.635 524.679 885.654C524.95 878.705 529.335 872.443 534.861 868.233C540.387 864.023 547.017 861.533 553.531 859.106C560.016 856.686 565.542 855.234 572.031 852.815L572.079 852.36C577.896 854.315 585.194 853.773 591.504 852.815C598.604 851.731 606.225 850.347 612.41 846.988L614.348 847.219Z"
            fill="#171717"
          />
          <path
            d="M489.997 867.621C491.397 871.357 489.32 875.528 486.658 878.49C479.118 886.871 467.02 889.911 455.783 889.059C444.546 888.207 433.891 883.968 423.438 879.777C418.85 877.938 414.127 876.022 410.594 872.567C405.533 867.65 403.607 860.197 403.878 853.142C404.149 846.309 406.249 839.689 408.397 833.185C415.259 840.095 426.245 842.534 435.826 840.173C440.229 839.055 444.389 837.139 448.099 834.52L448.225 834.694C457.477 845.607 468.876 854.499 481.712 860.817C485.003 862.433 488.7 864.191 489.997 867.621Z"
            :class="`text-${themeColor}-600 fill-current`"
          />
          <path
            d="M349.156 387.111C346.311 386.762 343.823 384.381 342.7 381.662C341.423 378.545 341.51 375.062 341.616 371.683L341.539 371.218C344.736 370.73 347.801 369.601 350.55 367.899C352.685 366.537 354.587 364.841 356.183 362.876L357.015 363.376C360.276 367.311 362.659 371.898 364.003 376.829C364.438 378.445 364.709 380.304 363.732 381.659C363.437 382.053 363.065 382.382 362.638 382.626C361.361 383.362 359.638 383.478 358.157 383.933C355.466 384.746 353.202 386.885 350.405 387.133C349.989 387.174 349.57 387.166 349.156 387.111V387.111Z"
            :class="`text-${themeColor}-600 fill-current`"
          />
          <path
            d="M354.003 481.002C359.407 465.801 361.76 449.684 360.928 433.573C360.096 417.462 356.095 401.674 349.154 387.111C349.568 387.168 349.987 387.178 350.403 387.14C353.203 386.888 355.464 384.749 358.155 383.94C359.636 383.485 361.355 383.369 362.636 382.633C380.43 413.87 386.478 450.436 379.69 485.739C377.957 494.546 375.354 503.363 370.224 510.729C361.93 522.662 347.983 529.529 338.924 540.887C336.688 530.667 338.73 519.952 342.176 510.071C345.622 500.19 350.441 490.845 354.003 481.002Z"
            :class="`text-${themeColor}-600 fill-current`"
          />
          <path
            d="M317.265 356.168C309.987 352.598 304.308 346.436 301.344 338.891C298.857 332.368 298.663 324.809 301.46 318.479C301.885 317.505 302.384 316.563 302.951 315.663C304.391 313.278 306.379 311.271 308.751 309.807C313.465 306.962 319.156 306.855 325.36 306.72C333.78 306.52 341.871 298.696 350.175 300.438C350.33 300.467 350.475 300.496 350.63 300.538C354.221 301.438 357.047 304.361 358.63 307.7C359.718 310.127 360.414 312.711 360.691 315.356C360.827 316.392 360.933 317.437 361.011 318.472C361.623 324.538 361.227 330.664 359.839 336.6C359.227 338.955 358.355 341.234 357.239 343.395C351.79 353.809 339.672 360.245 327.961 359.209C324.238 358.863 320.612 357.832 317.265 356.168ZM335.093 329.697C339.042 326.455 342.093 321.064 340.174 316.331C336.874 308.182 314.836 308.947 310.084 315.331C307.284 319.095 307.484 324.641 310.123 328.513C311.51 330.41 313.329 331.95 315.429 333.005C317.529 334.06 319.85 334.6 322.201 334.581C326.934 334.495 331.49 332.769 335.093 329.697V329.697Z"
            fill="#171717"
          />
          <path
            d="M320.089 327.036C318.404 326.362 317.012 325.111 316.162 323.508C315.311 321.904 315.057 320.05 315.444 318.277C315.753 317.009 316.595 315.702 317.883 315.528C319.034 315.373 320.07 316.148 321.096 316.68C321.531 316.911 321.988 317.096 322.461 317.232L322.422 318.277C321.836 318.84 321.368 319.515 321.046 320.262C320.724 321.009 320.554 321.812 320.547 322.626C320.54 323.439 320.695 324.245 321.004 324.998C321.313 325.75 321.769 326.433 322.345 327.007L322.325 327.442C321.562 327.432 320.807 327.295 320.089 327.036V327.036Z"
            fill="#171717"
          />
          <path
            d="M334.811 240.181C335.87 238.563 337.269 237.195 338.911 236.174C341.331 234.799 344.811 234.858 346.511 237.064C347.885 238.855 347.663 241.487 346.54 243.442C345.29 245.329 343.637 246.916 341.701 248.088C339.494 249.616 337.223 251.039 334.887 252.356C329.741 255.261 324.32 257.651 318.704 259.489C311.445 261.87 303.304 263.671 298.573 269.681C294.082 275.381 293.995 283.608 296.511 290.412C299.027 297.216 303.741 302.956 308.377 308.54L308.745 309.808C306.374 311.271 304.386 313.279 302.945 315.664C302.378 316.564 301.88 317.505 301.454 318.48L300.941 318.257C293.75 309.798 286.394 301.039 283.055 290.457C279.716 279.875 281.371 266.899 289.975 259.892C295.037 255.769 301.648 254.259 307.832 252.149C314.016 250.039 320.414 246.777 323.154 240.849C324.208 238.565 324.741 235.855 326.706 234.268C327.592 233.538 328.695 233.123 329.842 233.089C330.989 233.055 332.115 233.403 333.042 234.079C333.97 234.754 334.647 235.719 334.966 236.822C335.285 237.924 335.229 239.101 334.806 240.168C334.306 240.868 333.829 241.59 333.364 242.307C334.024 241.737 334.523 241.004 334.811 240.181V240.181Z"
            fill="#171717"
          />
          <path
            d="M151.297 593.554C144.933 600.851 141.532 610.267 141.763 619.947C142.141 630.506 146.574 640.456 153.271 648.721L152.081 649.699C145.065 661.477 138.044 673.259 131.02 685.045C128.736 688.868 126.258 692.885 122.29 694.878C115.535 698.285 107.046 694.346 102.439 688.355C97.0972 681.386 95.4612 672.134 96.1482 663.375C96.8352 654.616 99.6482 646.186 102.439 637.862C105.169 629.702 107.903 621.54 110.639 613.375C112.875 606.697 115.401 599.603 120.995 595.315C129.326 588.947 141.086 591.057 151.297 593.554Z"
            :class="`text-${themeColor}-600 fill-current`"
          />
          <path
            d="M325.095 305.908C327.331 304.204 329.56 302.497 331.783 300.788C327.009 292.536 322.764 283.989 319.075 275.198C318.41 273.904 318.087 272.461 318.136 271.007C318.465 268.287 321.272 266.555 323.936 265.887C326.6 265.219 329.462 265.187 331.901 263.922"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M313.392 283.899C312.422 283.553 311.392 283.404 310.363 283.458C309.334 283.513 308.326 283.771 307.398 284.217C306.469 284.663 305.638 285.289 304.953 286.058C304.267 286.828 303.741 287.725 303.404 288.699"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M326.613 278.682C328.132 274.482 333.988 272.604 337.666 275.14"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M336.486 253.169C344.04 267.901 348.687 283.95 350.171 300.439C350.18 300.464 350.183 300.491 350.181 300.517"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M308.747 309.808C306.376 311.271 304.387 313.278 302.947 315.664C302.38 316.563 301.881 317.505 301.456 318.48C298.656 324.81 298.856 332.369 301.34 338.892C304.305 346.435 309.983 352.598 317.261 356.168C320.605 357.829 324.227 358.86 327.946 359.207C339.657 360.243 351.775 353.807 357.224 343.393C358.34 341.231 359.212 338.952 359.824 336.598C361.212 330.661 361.608 324.535 360.996 318.47C360.918 317.435 360.812 316.39 360.676 315.354C360.399 312.708 359.703 310.124 358.615 307.698C357.037 304.359 354.215 301.436 350.615 300.536C350.46 300.497 350.315 300.468 350.16 300.436C341.86 298.694 333.76 306.514 325.345 306.718C319.152 306.856 313.461 306.963 308.747 309.808Z"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M331.469 314.618C330.371 315.865 328.941 316.774 327.345 317.237C325.749 317.7 324.054 317.698 322.459 317.232C321.986 317.097 321.529 316.912 321.094 316.68C320.068 316.148 319.032 315.373 317.881 315.528C316.593 315.702 315.751 317.009 315.442 318.277C315.055 320.051 315.309 321.904 316.16 323.508C317.01 325.112 318.402 326.362 320.087 327.036C320.805 327.295 321.561 327.432 322.323 327.442C323.75 327.473 325.158 327.106 326.388 326.383C327.619 325.66 328.624 324.609 329.292 323.348"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M322.345 327.007C321.769 326.434 321.313 325.751 321.004 324.998C320.695 324.246 320.54 323.439 320.547 322.626C320.554 321.813 320.724 321.009 321.046 320.262C321.368 319.516 321.836 318.841 322.422 318.277"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M310.084 315.335C307.284 319.099 307.484 324.645 310.123 328.517C311.51 330.413 313.329 331.953 315.429 333.008C317.529 334.063 319.85 334.604 322.201 334.585C326.934 334.497 331.491 332.77 335.093 329.697C339.042 326.455 342.093 321.064 340.174 316.331C336.871 308.182 314.836 308.947 310.084 315.335Z"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M334.811 240.181C335.234 239.114 335.29 237.937 334.971 236.834C334.652 235.732 333.975 234.767 333.048 234.091C332.12 233.416 330.994 233.068 329.847 233.102C328.7 233.136 327.597 233.551 326.711 234.281C324.746 235.868 324.211 238.581 323.159 240.862C320.419 246.785 314.012 250.047 307.837 252.162C301.662 254.277 295.037 255.782 289.98 259.905C281.38 266.912 279.712 279.881 283.06 290.47C286.408 301.059 293.76 309.807 300.946 318.27"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M334.811 240.181C334.311 240.881 333.834 241.603 333.369 242.32C334.03 241.746 334.527 241.008 334.811 240.181V240.181Z"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M308.379 308.54C303.743 302.956 299.02 297.226 296.513 290.412C294.006 283.598 294.084 275.382 298.575 269.681C303.308 263.671 311.447 261.87 318.706 259.489C324.322 257.65 329.743 255.261 334.889 252.356C337.222 251.04 339.494 249.617 341.703 248.088C343.639 246.915 345.292 245.329 346.542 243.442C347.665 241.487 347.887 238.855 346.513 237.064C344.819 234.858 341.335 234.799 338.913 236.174C337.271 237.195 335.871 238.562 334.813 240.181"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M361.814 315.199C371.174 268.403 380.526 221.603 389.872 174.799C390.791 170.173 391.807 165.353 394.73 161.646C398.689 156.633 405.193 154.784 411.745 154.503C413.611 154.432 415.478 154.468 417.339 154.61C423.263 155.055 429.283 156.168 435.158 155.91C437.429 155.832 439.683 155.491 441.874 154.893C437.938 187.143 433.995 219.385 430.047 251.621C425.711 287.121 421.366 322.729 421.588 358.481C421.898 409.881 431.654 460.764 443.394 510.822"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M316.577 357.491C312.4 358.006 308.17 357.898 304.024 357.172C287.967 354.452 273.324 343.08 265.924 328.301C256.613 309.728 257.697 287.07 265.624 267.868C273.551 248.666 287.724 232.59 303.651 219.253C308.035 215.585 313.068 210.087 310.464 204.996C309.448 203.022 307.483 201.754 305.606 200.583C305.412 200.457 305.206 200.331 305.015 200.215C299.257 196.605 293.502 192.998 287.749 189.394C286.616 188.688 285.455 187.972 284.255 187.323C281.196 185.659 277.935 184.429 274.555 184.875C269.764 185.504 266.096 189.298 262.931 192.946C239.761 219.698 221.565 251.637 215.642 286.538C209.719 321.439 217.181 359.417 239.993 386.488C257.793 407.597 283.062 420.566 308.081 432.5C309.681 433.265 311.266 434.019 312.853 434.774"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M305.563 199.328C310.209 199.495 314.76 197.979 318.377 195.059C319.936 193.791 321.377 192.088 321.416 190.075C321.474 187.133 318.377 184.916 315.435 184.829C312.493 184.742 309.744 186.155 307.15 187.529C314.871 176.755 320.514 164.635 323.788 151.791C324.059 150.707 324.281 149.439 323.565 148.577C323.135 148.169 322.573 147.928 321.981 147.896C321.389 147.864 320.804 148.044 320.332 148.403C319.407 149.157 318.683 150.128 318.222 151.229C313.384 160.579 308.548 169.925 303.714 179.268C308.359 167.753 318.222 145.03 313.87 143.541C307.97 140.991 305.97 153.558 303.959 159.641C301.948 165.724 300.417 172.078 296.894 177.43C300.118 166.887 302.169 156.022 303.011 145.03C303.166 143.075 302.811 140.481 300.872 140.143C298.933 139.805 297.726 142.175 297.194 144.082C293.194 158.474 289.194 172.869 285.194 187.267"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M411.698 153.374C407.117 150.623 403.176 146.924 400.142 142.524C399.203 141.169 398.409 139.195 399.571 138.014C400.461 137.114 401.961 137.327 403.181 137.685C407.6 138.981 411.704 141.172 415.24 144.121C415.24 128.991 414.544 98.6282 420.796 99.3101C427.048 99.9918 423.796 126.245 421.967 139.766C423.66 130.136 425.354 120.512 427.048 110.895C427.638 107.527 429.187 103.307 432.603 103.336C436.47 103.369 433.159 129.35 430.164 142.011C433.064 136.195 433.116 129.449 433.949 123.003C434.782 116.557 437.97 105.547 442.272 106.065C449.97 106.991 441.566 139.324 435.1 154.651"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M303.84 358.23C310.886 362.856 318.058 367.541 326.12 369.989C331.101 371.557 336.372 371.977 341.538 371.218C344.734 370.73 347.8 369.601 350.549 367.899C352.683 366.537 354.585 364.841 356.182 362.876C358.24 360.33 359.839 357.444 360.905 354.349C362.879 348.677 363.105 342.222 360.585 336.763"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M341.616 371.683C341.51 375.061 341.423 378.545 342.7 381.662C343.823 384.381 346.311 386.762 349.156 387.111C349.57 387.168 349.989 387.178 350.405 387.14C353.205 386.888 355.466 384.749 358.157 383.94C359.638 383.485 361.357 383.369 362.638 382.633C363.065 382.389 363.437 382.059 363.732 381.666C364.709 380.311 364.438 378.452 364.003 376.836C362.658 371.905 360.276 367.318 357.015 363.383"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M307.789 433.113C312.009 465.507 316.229 497.907 320.449 530.313C321.281 536.73 322.384 543.699 327.127 548.122C330.669 551.412 335.344 552.516 340.251 552.545C344.054 552.485 347.841 552.031 351.551 551.19C377.518 545.76 402.586 536.807 427.551 527.797C428.131 527.584 428.702 527.381 429.283 527.168C432.854 525.88 436.523 524.535 439.407 522.048C442.291 519.561 444.227 515.621 443.298 511.934"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M340.28 551.991L340.251 552.543C339.089 571.948 324.446 588.876 306.608 596.716C288.616 604.623 268.001 604.507 248.663 600.974C229.325 597.441 210.752 590.674 191.753 585.614C184.453 583.669 176.858 581.966 169.405 583.156C162.339 584.396 155.931 588.075 151.297 593.551C144.933 600.849 141.532 610.264 141.763 619.944C142.141 630.503 146.574 640.453 153.271 648.718C157.214 653.569 161.844 657.818 167.015 661.33C180.874 670.766 197.521 675.209 214.043 678.093C246.848 683.936 280.436 683.844 313.209 677.822C331.066 674.454 349.203 668.869 362.753 656.761C373.477 647.18 380.581 634.104 385.12 620.461C389.031 608.731 391.15 596.507 393.009 584.261C393.318 582.277 393.609 580.293 393.909 578.309"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M393.018 584.262C410.018 616.308 413.479 653.377 412.518 689.642C411.557 725.907 405.218 761.796 401.359 797.868C400.159 809.017 399.481 821.28 406.024 830.388C406.735 831.386 407.528 832.322 408.395 833.188C415.257 840.098 426.243 842.537 435.824 840.176C440.226 839.059 444.386 837.143 448.097 834.523C453.457 830.695 458.011 825.847 461.497 820.257C467.429 810.869 470.662 800.048 473.12 789.218C481.105 754.201 481.754 717.907 480.05 682.038C478.821 656.138 476.35 630.138 469.578 605.113C462.806 580.088 445.101 548.019 427.844 528.662"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M151.655 593.641L151.297 593.554C141.086 591.054 129.326 588.947 120.997 595.315C115.403 599.603 112.877 606.697 110.641 613.375C107.902 621.533 105.169 629.696 102.441 637.862C99.6544 646.186 96.8414 654.616 96.1504 663.375C95.4594 672.134 97.0994 681.386 102.441 688.355C107.048 694.346 115.541 698.285 122.292 694.878C126.26 692.885 128.738 688.868 131.022 685.045C138.04 673.257 145.06 661.475 152.083 649.699"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M408.601 832.585C408.533 832.785 408.465 832.985 408.401 833.185C406.253 839.685 404.153 846.309 403.882 853.142C403.611 860.197 405.537 867.65 410.598 872.567C414.131 876.022 418.854 877.938 423.442 879.777C433.895 883.968 444.56 888.197 455.787 889.059C467.014 889.921 479.122 886.871 486.662 878.49C489.324 875.528 491.405 871.357 490.001 867.621C488.701 864.195 485.007 862.433 481.716 860.821C468.878 854.502 457.478 845.607 448.225 834.691"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M652.984 265.055C658.137 267.27 663.568 268.771 669.127 269.516C666.127 280.337 663.134 291.158 660.146 301.978C661.627 301.988 663.098 301.988 664.579 301.998"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M659.41 278.082C659.347 277.336 659.062 276.625 658.593 276.04C658.124 275.456 657.491 275.024 656.776 274.801C656.06 274.578 655.294 274.573 654.576 274.787C653.858 275 653.219 275.423 652.742 276.001C653.042 275.788 653.342 275.565 653.651 275.343"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M675.623 279.534C675.816 279.388 676.01 279.253 676.194 279.108"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M683.558 282.234C683.302 281.464 682.87 280.763 682.298 280.187C681.726 279.611 681.029 279.176 680.26 278.914C679.492 278.653 678.673 278.573 677.868 278.68C677.064 278.788 676.295 279.08 675.622 279.534C675.496 279.621 675.37 279.708 675.254 279.805"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M663.029 313.491C665.662 315.691 669.794 315.252 672.572 313.239C673.085 312.865 673.564 312.448 674.004 311.991C675.886 309.904 677.272 307.419 678.06 304.723C680.548 306.232 682.443 308.548 683.431 311.285C683.907 312.649 684.048 314.108 683.84 315.539C683.633 316.969 683.085 318.328 682.241 319.502C681.653 320.264 680.919 320.902 680.083 321.38C679.256 321.867 678.334 322.173 677.379 322.276C676.425 322.38 675.459 322.279 674.546 321.98"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M679.908 320.774C679.85 318.921 679.272 317.122 678.24 315.582C677.208 314.042 675.764 312.823 674.072 312.063"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M659.14 343.411C662.605 338.833 660.863 332.059 657.388 327.5C653.913 322.941 649.026 319.632 645.397 315.189C639.297 307.727 637.315 297.574 637.954 287.964C638.593 278.354 642.6 266.177 645.61 257.021"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M685.756 349.045C691.323 328.279 694.345 306.913 694.756 285.418C694.834 281.063 694.747 276.466 692.511 272.73C689.269 267.3 682.552 265.258 676.435 263.641C666.281 260.97 656.128 258.296 645.976 255.618C642.966 254.824 639.953 254.03 636.937 253.237C634.686 252.793 632.538 251.93 630.607 250.692C628.768 249.337 627.423 247.052 627.849 244.807C628.603 240.819 634.091 239.726 637.875 241.207C641.659 242.688 644.709 245.727 648.58 246.956C646.838 244.923 645.038 242.784 644.37 240.181C643.702 237.578 644.534 234.374 646.954 233.202C649.267 232.08 652.103 233.183 654.01 234.896C655.917 236.609 657.262 238.884 659.062 240.713C663.417 245.107 669.882 246.443 675.883 247.924C682.609 249.586 689.22 251.68 695.676 254.195C698.424 255.26 701.276 256.518 702.993 258.918C706.129 263.318 704.144 269.342 702.886 274.607C700.051 286.783 701.889 299.579 708.035 310.466C709.148 312.421 710.335 315.066 708.78 316.68C707.203 318.306 704.348 317.086 702.915 315.325C701.482 313.564 700.505 311.279 698.473 310.253C698.744 312.837 700.534 314.967 701.628 317.319C702.722 319.671 702.847 323.01 700.612 324.336C698.899 325.362 696.566 324.578 695.212 323.097C693.98 321.513 693.125 319.669 692.712 317.706"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M640.537 352.897L640.431 352.868C640.413 352.859 640.393 352.856 640.373 352.858C627.955 349.49 615.441 346.083 603.933 340.315C592.386 334.515 581.779 326.097 575.555 314.773C567.309 299.773 567.609 281.508 570.668 264.667C573.727 247.826 579.32 231.431 580.995 214.397C581.401 210.255 581.314 205.454 578.149 202.754C577.142 201.93 575.966 201.338 574.704 201.021C573.389 200.674 572.049 200.429 570.697 200.286C564.23 199.463 557.762 198.634 551.291 197.798L547.662 197.334C543.568 196.811 539.125 196.356 535.573 198.447C532.079 200.499 530.308 204.525 529.002 208.358C521.249 230.976 521.83 255.541 523.949 279.35C526.243 305.24 530.502 331.672 543.558 354.15C556.558 376.55 579.94 394.433 605.81 394.51H606.101"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M700.797 370.415C712.982 371.615 725.39 372.815 737.382 370.367C749.374 367.919 761.123 361.23 766.682 350.322C772.635 338.611 770.525 324.655 769.189 311.599C766.237 282.757 767.699 253.024 758.475 225.547C757.362 222.217 756.075 218.878 755.833 215.365C755.591 211.852 756.588 208.048 759.298 205.812C761.785 203.77 765.163 203.393 768.425 203.257C768.763 203.238 769.102 203.218 769.431 203.209C776.535 202.976 783.646 202.741 790.763 202.502C790.82 202.494 790.879 202.49 790.937 202.492C797.137 202.28 804.129 202.367 808.484 206.792C812.084 210.45 812.752 215.992 813.207 221.097C817.388 267.844 819.595 314.94 815.346 361.677C814.591 369.977 813.585 378.412 810.207 386.038C806.035 395.438 798.476 402.976 790.075 408.909C773.997 420.172 754.804 426.134 735.175 425.962C733.375 425.943 731.584 425.872 729.803 425.749"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M606.024 393.491C605.957 393.829 605.879 394.168 605.811 394.507C599.433 424.578 593.075 454.717 589.711 485.263C588.743 494.063 588.075 503.263 591.311 511.501C593.924 518.141 598.725 523.377 604.348 527.858C608.75 531.242 613.359 534.348 618.148 537.158C620.325 538.484 623.355 539.742 625.174 537.952C625.974 536.967 626.431 535.746 626.474 534.477L627.694 525.853C628.178 522.398 628.665 518.943 629.155 515.488C632.872 489.172 636.585 462.856 640.295 436.54C642.744 419.157 645.202 401.755 646.102 384.227C646.654 373.416 646.412 361.841 640.373 352.859"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M698.899 370.638C697.787 422.079 697.174 473.53 697.06 524.991C697.05 529.279 697.047 533.563 697.05 537.844C697.04 543.835 697.04 549.826 697.05 555.817C708.205 548.865 720.842 544.64 733.935 543.487C737.816 543.148 742.055 542.964 744.988 540.387C749.624 536.331 748.443 528.976 746.988 522.987C739.395 491.543 735.427 459.333 735.161 426.987"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M640.508 352.597C637.788 350.322 640.402 344.486 643.576 342.897C646.75 341.308 650.535 341.978 654.019 342.684C655.703 343.023 657.397 343.362 659.081 343.7C667.976 345.482 676.867 347.269 685.755 349.062C686.171 349.149 686.588 349.227 687.004 349.314C690.314 349.972 693.837 350.756 696.198 353.176C699.944 356.999 700.234 364.141 699.305 369.416"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M603.731 528.659C565.655 625.841 551.592 730.999 561.184 834.936C561.552 838.895 562.006 843.018 564.126 846.386C565.989 849.25 568.803 851.365 572.072 852.358C577.889 854.313 585.187 853.771 591.497 852.813C598.597 851.729 606.218 850.345 612.403 846.986C615.665 845.286 618.483 842.842 620.629 839.853C624.017 835.024 625.129 828.994 625.991 823.167C632.36 780.067 629.543 736.186 632.031 692.691C634.46 650.009 637.664 614.45 662.189 579.955C662.644 579.307 663.118 578.649 663.589 578"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M662.508 580.178C661.182 667.004 659.859 753.834 658.54 840.666C658.463 845.525 658.453 850.635 660.84 854.866C663.017 858.708 666.569 860.982 670.721 862.266C676.19 863.966 682.703 863.966 688.511 863.601C696.592 863.101 705.38 862.188 712.746 859.149C717.795 857.23 722.128 853.799 725.154 849.325C729.867 842.025 729.828 832.746 729.554 824.055C726.654 731.305 714.736 638.517 721.598 545.981"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M630.385 515.661C652.101 521.239 674.373 524.369 696.785 524.991"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M627.615 525.833C627.64 525.844 627.666 525.85 627.693 525.852C650.102 533.215 673.469 537.255 697.049 537.844C697.12 537.852 697.191 537.856 697.262 537.854"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M657.671 347.429C657.671 347.603 657.661 347.777 657.661 347.951C657.555 353.555 657.452 359.165 657.352 364.782C660.991 361.582 664.633 358.378 668.279 355.171C668.743 354.765 669.247 354.171 668.995 353.613C668.799 353.304 668.495 353.08 668.143 352.984C664.862 351.629 661.591 349.316 658.31 347.961"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M650.302 405.974C652.902 431.712 657.399 457.223 663.755 482.299C665.023 487.274 666.416 492.336 669.33 496.565C672.244 500.794 677.014 504.076 682.144 503.882C683.286 498.772 684.425 493.662 685.561 488.551C672.718 455.554 665.836 420.539 665.236 385.136C665.149 379.948 666.765 373.657 662.845 369.883C660 367.134 656.264 366.302 653.476 369.35C646.392 377.122 649.344 396.644 650.302 405.974Z"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M572.023 852.813C565.538 855.232 560.012 856.684 553.523 859.104C547.009 861.533 540.389 864.02 534.853 868.231C529.317 872.442 524.942 878.703 524.671 885.652C524.555 888.633 525.329 891.852 527.623 893.763C529.336 895.195 531.623 895.679 533.808 896.063C555.467 899.774 577.731 897.385 598.108 889.163C604.883 886.415 611.784 882.669 615.308 876.271C620.128 867.532 617.282 856.74 614.34 847.217"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M670.493 862.975C662.469 868.937 654.443 874.899 646.413 880.861C640.722 885.091 634.925 889.436 630.947 895.301C628.779 898.495 627.172 902.28 627.547 906.112C628.06 911.339 632.203 915.636 636.974 917.812C646.953 922.351 658.654 919.06 668.749 914.812C679.644 910.231 690.061 904.587 699.849 897.962C705.705 894.004 711.58 889.387 714.249 882.854C717.23 875.554 715.691 867.3 714.113 859.587"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M559.316 177.919C561.222 167.519 564.47 146.802 559.442 146.802C553.471 145.491 550.436 180.113 551.35 197.344"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M559.229 178.374C558.987 179.574 558.764 180.774 558.551 181.974C558.783 180.784 559.006 179.583 559.229 178.374Z"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M559.229 178.374C559.258 178.219 559.286 178.064 559.315 177.919"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M574.828 200.615C576.503 198.786 578.71 197.547 580.694 196.066C582.678 194.585 584.565 192.688 585.078 190.266C585.591 187.844 584.217 184.894 581.768 184.516C578.216 183.965 575.961 188.533 572.477 189.404C576.354 178.948 579.036 168.087 580.471 157.029C580.723 155.152 580.481 152.606 578.613 152.248C576.803 151.9 575.622 154.068 575.07 155.829C571.974 165.633 568.88 175.438 565.789 185.242C567.947 174.102 570.105 162.904 570.56 151.561C570.618 150.1 569.96 148.028 568.537 148.386C567.783 148.57 567.405 149.393 567.144 150.119C563.812 159.166 561.194 168.462 559.314 177.919"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M790.729 201.738C789.961 188.442 788.214 175.22 785.502 162.181C784.854 159.081 784.05 155.803 781.728 153.655C780.276 152.3 777.628 151.7 776.511 153.345C776.025 154.29 775.891 155.378 776.133 156.413C777.343 166.653 778.549 176.893 779.753 187.133C778.398 177.851 776.966 168.347 772.407 160.149C771.736 158.718 770.684 157.499 769.368 156.626C768.698 156.207 767.905 156.03 767.122 156.125C766.338 156.221 765.61 156.582 765.061 157.149C764.061 158.416 764.451 160.236 764.887 161.785C767.403 170.699 769.919 179.61 772.436 188.517C769.736 180.425 767.016 172.276 763 164.737C762.49 163.619 761.739 162.629 760.8 161.837C760.321 161.448 759.735 161.217 759.12 161.174C758.505 161.13 757.892 161.277 757.364 161.595C756.057 162.543 756.164 164.508 756.454 166.095C757.974 174.577 760.505 182.846 763.993 190.726C762.155 189.497 760.199 188.22 757.993 188.142C755.787 188.064 753.366 189.742 753.502 191.936C753.618 193.843 755.438 195.13 757.064 196.127C760.848 198.421 764.629 200.721 768.407 203.027"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M349.098 386.991L349.156 387.108C356.097 401.671 360.098 417.459 360.93 433.57C361.762 449.681 359.409 465.798 354.005 480.999C350.443 490.842 345.623 500.192 342.178 510.064C338.733 519.936 336.69 530.664 338.926 540.88C347.985 529.518 361.932 522.655 370.226 510.722C375.356 503.356 377.959 494.539 379.692 485.732C386.48 450.429 380.432 413.863 362.638 382.626C362.609 382.578 362.58 382.52 362.551 382.472"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M860.179 386.943C842.806 418.03 827.725 451.534 825.784 487.093C825.347 495.104 825.693 503.555 829.752 510.476C833.811 517.397 842.62 522.102 850.076 519.138C857.532 516.174 859.812 503.968 852.813 500.045C847.453 497.045 840.287 500.468 837.234 505.801C834.181 511.134 834.218 517.647 834.69 523.774C835.754 537.604 838.599 551.313 838.539 565.183C838.479 579.053 835.096 593.618 825.512 603.646C821.019 608.346 814.197 611.992 808.137 609.632C802.077 607.272 800.832 596.579 807.116 594.902"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M797.418 423.755C803.108 422.312 808.254 419.241 812.225 414.917C816.195 410.593 818.818 405.205 819.771 399.412"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M539.037 706.832C536.685 698.303 536.232 689.363 537.709 680.64C539.186 671.918 542.558 663.625 547.586 656.347"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M536.653 385.376C529.918 381.522 524.274 376.02 520.248 369.386C516.222 362.753 513.947 355.205 513.637 347.452"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M543.314 123.337C552.883 115.561 561.181 106.341 567.909 96.0088"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M502.219 106.931L507.928 40"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M469.53 101.475L459.971 83.7119"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M707.387 602.263L708.562 589.263"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M679.27 642.562V629.581"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M703.87 700.633L703.203 688.333"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M679.057 788.73L680.512 779.863"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M712.062 760.754L711.379 747.09"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M627.346 606.353C628.425 601.494 630.784 597.012 634.178 593.372"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M608.818 563.298C609.536 559.227 611.769 555.58 615.069 553.091"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M587.039 685.603L590.455 673.303"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M610.949 751.191C612.597 747.294 612.838 742.946 611.632 738.891"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M569.227 785.364C568.762 783.907 568.59 782.372 568.72 780.848C568.85 779.323 569.28 777.84 569.985 776.482"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M602.77 828.391L604.133 817.459"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M668.359 533.938L669.722 525.738"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M653.309 532.568L655.358 523.687"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M294.27 227.07C286.623 219.788 277.686 213.994 267.917 209.984C265.941 209.174 263.787 208.318 262.693 206.484"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M390.328 177.622C403.722 175.232 417.333 174.287 430.928 174.801"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M252.276 177.537L247.266 168.883"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M244.592 176.697L253.576 169.258"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M268.689 137.912C268.635 134.105 267.685 130.364 265.917 126.991"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M261.385 133.813L272.002 128.358L271.245 129.623"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M189.371 203.975C180.429 212.375 172.796 222.591 169.457 234.399C166.118 246.207 167.57 259.753 175.147 269.405C178.597 273.799 184.124 277.472 189.507 275.979C195.763 274.244 198.286 266.764 198.899 260.302C199.285 256.227 199.28 251.978 197.674 248.213C196.068 244.448 192.536 241.27 188.444 241.142C182.253 240.949 177.925 247.316 176.214 253.269C173.44 263.066 174.275 273.534 178.567 282.769C180.677 287.263 183.653 291.64 183.689 296.605C183.747 304.689 175.577 310.891 167.526 311.628C159.475 312.365 151.653 309.119 144.221 305.939"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M192.927 324.68C190.772 347.61 201.835 371.319 220.792 384.397"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M235.371 454.119C249.734 471.575 260.831 491.478 268.131 512.872C271.684 523.302 274.293 534.809 270.621 545.197C268.747 550.494 265.349 555.119 260.855 558.49C256.36 561.862 250.97 563.83 245.36 564.147C239.75 564.464 234.172 563.116 229.327 560.272C224.481 557.428 220.584 553.215 218.126 548.163C214.54 540.789 215.288 530.19 222.566 526.414C228.592 523.288 236.772 526.898 239.38 533.166C241.988 539.434 239.431 547.143 234.18 551.448C228.929 555.753 221.541 556.887 214.908 555.441C206.608 553.632 199.594 548.273 191.88 544.712C184.166 541.151 174.27 539.687 167.764 545.15"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M318.648 699.3C326.535 700.509 334.594 699.903 342.212 697.529C349.83 695.155 356.806 691.075 362.609 685.6"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M721.285 249.044L734.607 238.796"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M731.534 251.091L722.311 237.771"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M684.393 223.424V214.201"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M675.178 220.491L691.578 219.573"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M707.328 837.96L708.679 826.116"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M127.471 591.996C116.545 613.91 106.33 637.427 107.807 661.869C108.186 668.152 109.407 674.543 112.685 679.914C115.963 685.285 121.601 689.501 127.885 689.825"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M145.023 660.114L134.446 650.662"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M138.815 669.942L130.715 662.101"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M404.809 862.01C413.063 865.966 421.455 869.643 430.028 872.846C439.828 876.506 450.128 879.58 460.564 878.937C471 878.294 481.683 873.308 486.747 864.158"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M466.606 853.298C463.648 855.541 460.844 857.979 458.213 860.598L458.77 860.332"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M456.718 844.441C453.919 845.885 451.478 847.935 449.57 850.441"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M420.769 227.65C419.36 225.298 417.332 223.379 414.906 222.102"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M386.18 268.173L390.735 263.619"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M411.231 300.967L407.131 296.412"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M380.615 346.427L386.105 340.072"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M410.171 394.964L404.799 390.302"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M398.57 459.516L402.342 452.296"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M417.151 502.28L410.775 498.18"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M337.446 461.744C335.348 461.658 333.309 461.031 331.525 459.922"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M324.197 418.45C325.61 416.243 326.58 413.781 327.051 411.203"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M295.999 380.672L290.533 377.939"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M253.424 375.556L257.175 367.865"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M248.637 327.805C245.905 327.331 243.361 326.099 241.295 324.25"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M228.663 291.918L233.202 286.439"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M258.196 251.777L254.096 246.311"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M229.729 496.359L223.581 487.478"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M220.043 498.182C224.645 495.694 228.497 492.02 231.201 487.541"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M155.945 268.857C153.057 269.137 150.161 268.413 147.745 266.807"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M149.797 273.639L152.53 263.391"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M863.459 554.002L857.49 547.684"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M856.898 557.163L863.046 548.282"
            stroke="#171717"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <g opacity="0.1">
            <path
              d="M274.236 188.017C268.267 190.942 264.022 196.199 259.687 201.136C255.245 206.196 251.037 211.485 247.049 216.909C238.903 228.174 232.292 240.472 227.387 253.479C217.976 278.019 214.38 305.203 218.933 331.158C223.305 355.915 234.859 378.84 252.157 397.082C260.665 405.979 270.622 413.366 281.603 418.93C291.78 424.046 305.327 428.223 310.013 439.709C313.133 447.355 313.53 456.345 314.569 464.446C315.747 473.627 316.91 482.809 318.147 491.982L321.838 519.362C323.101 528.732 323.796 538.425 326.138 547.589C327.991 554.844 339.103 553.275 339.121 545.832C339.139 537.978 337.821 530.099 336.929 522.298C335.961 513.8 334.991 505.302 334.021 496.804C332.256 481.329 330.486 465.846 328.552 450.391C326.733 435.847 322.232 424.021 309.825 415.418C300.014 408.618 288.351 405.079 278.418 398.405C237.765 371.09 223.876 315.739 236.263 270.177C240.127 256.085 245.8 242.553 253.14 229.918C256.883 223.292 260.976 216.87 265.403 210.679C269.803 204.652 275.597 199.329 278.79 192.566C279.135 191.93 279.266 191.199 279.162 190.482C279.058 189.765 278.725 189.101 278.213 188.589C277.701 188.077 277.037 187.744 276.32 187.641C275.603 187.537 274.872 187.668 274.236 188.013V188.017Z"
              fill="#171717"
            />
          </g>
          <g opacity="0.1">
            <path
              d="M403.659 155.089C397.702 156.552 396.207 165.872 394.644 170.855C392.344 178.193 390.033 185.54 387.938 192.94C383.752 207.719 380.107 222.725 376.847 237.733C373.58 252.773 370.635 267.882 367.767 283.003C366.393 290.245 365.083 297.499 363.836 304.765C362.877 310.355 359.076 320.537 364.153 324.885C364.592 325.325 365.14 325.641 365.741 325.799C366.342 325.957 366.975 325.952 367.573 325.785C373.956 323.095 373.596 314.723 374.633 308.73C375.945 301.152 377.326 293.585 378.774 286.03C381.674 270.93 385.041 255.93 388.635 240.976C392.229 226.022 395.996 211.11 399.758 196.192C401.558 189.043 403.281 181.874 405.136 174.738C406.529 169.378 410.546 160.47 406.167 155.745C405.844 155.424 405.443 155.194 405.002 155.077C404.562 154.961 404.099 154.963 403.659 155.082V155.089Z"
              fill="#171717"
            />
          </g>
          <g opacity="0.1">
            <path
              d="M605.547 532.566C599.96 540.96 597.36 552.145 594.247 561.671C590.979 571.671 587.939 581.738 585.203 591.897C579.815 611.909 575.315 632.132 571.703 652.566C564.351 693.702 560.537 735.393 560.303 777.181C560.318 800.704 561.613 824.209 564.184 847.592C565.076 856.05 578.374 856.237 577.59 847.592C570.162 765.641 576.032 682.465 596.604 602.739C599.537 591.362 602.711 580.046 606.125 568.793C609.425 557.904 614.585 546.678 615.858 535.358C616.521 529.466 608.285 528.458 605.551 532.566H605.547Z"
              fill="#171717"
            />
          </g>
          <g opacity="0.1">
            <path
              d="M665.209 579.585C663.545 583.62 663.447 588.353 663.053 592.674C662.629 597.321 662.545 601.99 662.386 606.651C662.067 615.973 662.016 625.277 661.998 634.604C661.964 652.904 661.882 671.204 661.934 689.511L662.251 801.324L662.339 832.272L662.382 847.246C662.397 852.74 661.756 858.293 664.628 863.206C665.159 864.082 665.908 864.807 666.801 865.31C667.695 865.814 668.703 866.078 669.728 866.078C670.753 866.078 671.761 865.814 672.654 865.31C673.548 864.807 674.296 864.082 674.828 863.206C677.242 859.043 677.034 854.906 677.028 850.241L677.018 836.265L676.999 808.312L676.96 753.404C676.932 716.133 676.844 678.862 676.693 641.592C676.637 631.276 676.708 620.954 676.319 610.644C676.131 605.653 676.032 600.651 675.684 595.669C675.321 590.482 675.262 584.411 673.253 579.585C671.621 575.665 666.836 575.636 665.207 579.585H665.209Z"
              fill="#171717"
            />
          </g>
          <defs>
            <linearGradient
              id="paint0_linear_248_1702"
              x1="643.965"
              y1="525.854"
              x2="643.965"
              y2="863.803"
              gradientUnits="userSpaceOnUse"
            >
              <stop stop-color="#4A9FFF" />
              <stop
                offset="1"
                stop-color="#2A67F7"
              />
            </linearGradient>
            <linearGradient
              id="paint1_linear_248_1702"
              x1="447.15"
              y1="833.185"
              x2="447.15"
              y2="889.197"
              gradientUnits="userSpaceOnUse"
            >
              <stop stop-color="#4A9FFF" />
              <stop
                offset="1"
                stop-color="#2A67F7"
              />
            </linearGradient>
            <linearGradient
              id="paint2_linear_248_1702"
              x1="352.968"
              y1="362.876"
              x2="352.968"
              y2="387.159"
              gradientUnits="userSpaceOnUse"
            >
              <stop stop-color="#4A9FFF" />
              <stop
                offset="1"
                stop-color="#2A67F7"
              />
            </linearGradient>
            <linearGradient
              id="paint3_linear_248_1702"
              x1="360.196"
              y1="382.633"
              x2="360.196"
              y2="540.887"
              gradientUnits="userSpaceOnUse"
            >
              <stop stop-color="#4A9FFF" />
              <stop
                offset="1"
                stop-color="#2A67F7"
              />
            </linearGradient>
            <linearGradient
              id="paint4_linear_248_1702"
              x1="124.636"
              y1="591.108"
              x2="124.636"
              y2="696.139"
              gradientUnits="userSpaceOnUse"
            >
              <stop stop-color="#4A9FFF" />
              <stop
                offset="1"
                stop-color="#2A67F7"
              />
            </linearGradient>
          </defs>
        </svg>
      </div>
    </div>
  </main>
</template>

<script lang="ts">
  import { computed, defineComponent, PropType } from 'vue'
  import { downloadDocument } from '@/lib/utils'
  import { useRouter, useRoute } from 'vue-router'
  import { useThemeColor } from '@/composables/useThemeColor'
  import { useTranslation } from '@/composables/useTranslation'
  import { ROUTES } from '@/router/routes'

  export default defineComponent({
    props: {
      handleDownloadClick: {
        type: Function as unknown as () => (payload: MouseEvent) => void,
        required: true
      },
      isStandalone: {
        type: Boolean as PropType<boolean>,
        required: false,
        default: false
      },
      isExternal: {
        type: Boolean as PropType<boolean>,
        required: false,
        default: false
      }
    },
    setup(props) {
      const router = useRouter()
      const route = useRoute()
      const { t } = useTranslation({
        en: {
          documentCompleted: 'Document completed',
          documentCompletedMessage:
            'Great, this document has already been filled out, signed and submitted.',
          needCopy: 'Do you need a copy for your records?',
          backToPortal: 'Back to client portal',
          pdfDownload: 'PDF Download'
        },
        de: {
          documentCompleted: 'Dokument abgeschlossen',
          documentCompletedMessage:
            'Super, dieses Dokument ist bereits fertig ausgefüllt, unterzeichnet und abgeschickt.',
          needCopy: 'Benötigen Sie eine Kopie für Ihre Unterlagen?',
          backToPortal: 'Zurück zum Mandantenportal',
          pdfDownload: 'PDF Download'
        }
      })

      const isExternalRoute = computed(() => {
        return route.path.includes('/external/')
      })
      const themeColor = computed(() => useThemeColor().value)

      // Show back button if user has a session token (came from client portal)
      // or if it's not a standalone/external submission
      const showBackButton = computed(() => {
        const hasSessionToken = !!route.query.session_token
        const isRegularSubmission =
          !isExternalRoute.value && !props.isStandalone && !props.isExternal
        return hasSessionToken || isRegularSubmission
      })

      const navigateToHome = () => {
        // Build query params - prefer session_token if available, fallback to access_token
        const queryParams: Record<string, string> = {}
        if (route.query.session_token) {
          queryParams.session_token = route.query.session_token.toString()
        } else if (route.query.access_token) {
          queryParams.access_token = route.query.access_token.toString()
        }
        router.push({
          path: `${ROUTES.CLIENT_PORTAL.HOME}`,
          query: queryParams
        })
      }

      return {
        t,
        themeColor,
        navigateToHome,
        isExternalRoute,
        showBackButton
      }
    }
  })
</script>
